/**
 * The document types a device session's report may be filed under — PROD-2374.
 *
 * THE DECISION (Andrew, 2026-09-19)
 * ---------------------------------
 * A session report is EITHER `Radiology` OR `Procedure`, and the technician
 * picks which. The fleet is mixed: a DEXA or an ultrasound produces a
 * radiology report, while an HBOT or cryotherapy session produces a procedure
 * record. There are 57 active devices and they do not all make the same kind of
 * artefact, so the drawer that was shipped locked to `Procedure` was an
 * unverified guess that mis-types half of them.
 *
 * THIS LIST IS THE AGREEMENT BETWEEN TWO SIDES
 * --------------------------------------------
 * It is imported by BOTH halves of the session↔report link and must never be
 * inlined at either:
 *
 *   - the WRITE — `technician-worklist-tab.tsx` passes it to `NewFilePopup` as
 *     `allowedTypes`, which is what the attach drawer will let a technician
 *     save under;
 *   - the READ — `use-session-reports.ts` sends it as the `fileType` filter on
 *     `POST /api/Patients/GetDocumentsByAppointments`.
 *
 * If those two ever disagree the failure is SILENT: the document saves
 * successfully, the server filters it back out, and the worklist says "no
 * report" for a session that has one. Nothing errors, nothing logs, and it is
 * invisible until a reload. That is the exact defect this ticket exists to
 * close — and one this branch already hit once, when the write did not set
 * `appointmentId` at all. One constant, two importers, so they cannot drift.
 *
 * DO NOT DROP THE READ FILTER
 * ---------------------------
 * "Just remove the `fileType` filter" is not the easy version of this change.
 * It was verified live on dev that an unfiltered read of a session's
 * appointment returns a **Lab** document, so an unfiltered read would offer a
 * lab result as the session's report. The filter is what keeps a document this
 * surface did not produce from being presented as its output.
 *
 * ORDER MATTERS IN EXACTLY ONE WAY
 * --------------------------------
 * The tab row renders in `TYPE_OPTIONS_V1` order, not this one; the wire
 * filter is a set. The only thing this order decides is the drawer's INITIAL
 * type (`allowedTypes[0]`) — reorder it and you change what a technician saves
 * when they do not touch the picker.
 */

import {
  DocumentType,
  type DocumentTypeCode,
} from '@/documents/types/document';

export const SESSION_REPORT_TYPES: readonly DocumentTypeCode[] = [
  DocumentType.Radiology,
  DocumentType.Procedure,
] as const;
